import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Route as RouteIcon, MapPin, Calendar, Building2, TrendingUp, Ruler, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Haversine formula to calculate distance between two coordinates
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const StatsPage = () => {
  const { projectId } = useParams();
  const [stats, setStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchAdvancedStats();
  }, [projectId]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas básicas');
    }
  };

  const fetchAdvancedStats = async () => {
    setLoading(true);
    try {
      // Fetch all necessary data
      const [routesRes, tripsRes, shapesRes, stopsRes, stopTimesRes, calendarRes, agencyRes] = await Promise.all([
        axios.get(`${API_URL}/projects/${projectId}/records/routes`),
        axios.get(`${API_URL}/projects/${projectId}/records/trips`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/projects/${projectId}/records/shapes`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/projects/${projectId}/records/stops`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/projects/${projectId}/records/stop_times`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/projects/${projectId}/records/calendar`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/projects/${projectId}/records/agency`).catch(() => ({ data: [] }))
      ]);

      const routes = routesRes.data;
      const trips = tripsRes.data;
      const shapes = shapesRes.data;
      const stops = stopsRes.data;
      const stopTimes = stopTimesRes.data;
      const calendar = calendarRes.data;
      const agencies = agencyRes.data;

      // Calculate stats by route
      const routeStats = routes.map(route => {
        const routeTrips = trips.filter(t => t.data.route_id === route.data.route_id);
        
        // Calculate distance from shapes
        let distance = 0;
        
        // Try to find shapes by route's shape_id or by matching route_id in shape_id
        let shapePoints = [];
        if (route.data.shape_id) {
          shapePoints = shapes.filter(s => s.data.shape_id === route.data.shape_id);
        }
        
        // If no shapes found with exact match, try to find by route_id pattern in shape_id
        if (shapePoints.length === 0) {
          const routeId = route.data.route_id;
          shapePoints = shapes.filter(s => 
            s.data.shape_id && s.data.shape_id.includes(routeId)
          );
        }
        
        if (shapePoints.length > 0) {
          shapePoints.sort((a, b) => parseInt(a.data.shape_pt_sequence || 0) - parseInt(b.data.shape_pt_sequence || 0));
          
          for (let i = 0; i < shapePoints.length - 1; i++) {
            const p1 = shapePoints[i];
            const p2 = shapePoints[i + 1];
            if (p1.data.shape_pt_lat && p1.data.shape_pt_lon && p2.data.shape_pt_lat && p2.data.shape_pt_lon) {
              const lat1 = parseFloat(p1.data.shape_pt_lat);
              const lon1 = parseFloat(p1.data.shape_pt_lon);
              const lat2 = parseFloat(p2.data.shape_pt_lat);
              const lon2 = parseFloat(p2.data.shape_pt_lon);
              
              // Only calculate if coordinates are valid
              if (!isNaN(lat1) && !isNaN(lon1) && !isNaN(lat2) && !isNaN(lon2)) {
                distance += calculateDistance(lat1, lon1, lat2, lon2);
              }
            }
          }
        }

        // Calculate trips per service
        const tripsPerService = {};
        routeTrips.forEach(trip => {
          const serviceId = trip.data.service_id;
          if (!tripsPerService[serviceId]) {
            tripsPerService[serviceId] = 0;
          }
          tripsPerService[serviceId]++;
        });

        // Calculate weekly frequency
        let weeklyTrips = 0;
        Object.entries(tripsPerService).forEach(([serviceId, tripCount]) => {
          const service = calendar.find(c => c.data.service_id === serviceId);
          if (service) {
            const daysActive = [
              service.data.monday,
              service.data.tuesday,
              service.data.wednesday,
              service.data.thursday,
              service.data.friday,
              service.data.saturday,
              service.data.sunday
            ].filter(d => d === '1').length;
            weeklyTrips += tripCount * daysActive;
          }
        });

        return {
          route_id: route.data.route_id,
          route_name: route.data.route_short_name || route.data.route_long_name,
          distance: distance.toFixed(2),
          total_trips: routeTrips.length,
          weekly_trips: weeklyTrips,
          agency_id: route.data.agency_id
        };
      });

      // Calculate stats by agency
      const agencyStats = agencies.map(agency => {
        const agencyRoutes = routeStats.filter(r => r.agency_id === agency.data.agency_id);
        const totalDistance = agencyRoutes.reduce((sum, r) => sum + parseFloat(r.distance || 0), 0);
        const totalTrips = agencyRoutes.reduce((sum, r) => sum + r.total_trips, 0);
        const totalWeeklyTrips = agencyRoutes.reduce((sum, r) => sum + r.weekly_trips, 0);

        return {
          agency_name: agency.data.agency_name,
          agency_id: agency.data.agency_id,
          total_routes: agencyRoutes.length,
          total_distance: totalDistance.toFixed(2),
          total_trips: totalTrips,
          weekly_trips: totalWeeklyTrips
        };
      });

      // Calculate service periods
      const servicePeriods = calendar.map(cal => {
        const serviceTrips = trips.filter(t => t.data.service_id === cal.data.service_id);
        const daysActive = [
          { day: 'Lunes', active: cal.data.monday === '1' },
          { day: 'Martes', active: cal.data.tuesday === '1' },
          { day: 'Miércoles', active: cal.data.wednesday === '1' },
          { day: 'Jueves', active: cal.data.thursday === '1' },
          { day: 'Viernes', active: cal.data.friday === '1' },
          { day: 'Sábado', active: cal.data.saturday === '1' },
          { day: 'Domingo', active: cal.data.sunday === '1' }
        ].filter(d => d.active).map(d => d.day);

        return {
          service_id: cal.data.service_id,
          start_date: cal.data.start_date,
          end_date: cal.data.end_date,
          days_active: daysActive,
          total_trips: serviceTrips.length
        };
      });

      setAdvancedStats({
        routeStats,
        agencyStats,
        servicePeriods,
        totalDistance: routeStats.reduce((sum, r) => sum + parseFloat(r.distance || 0), 0).toFixed(2)
      });
    } catch (error) {
      console.error('Error calculating advanced stats:', error);
      toast.error('Error al calcular estadísticas avanzadas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-slate-600">Calculando estadísticas...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6 md:p-8">
        <Card className="p-8">
          <p className="text-center text-slate-500">No hay estadísticas disponibles</p>
        </Card>
      </div>
    );
  }

  const metrics = [
    {
      icon: RouteIcon,
      label: 'Rutas',
      value: stats.total_routes,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: MapPin,
      label: 'Paradas',
      value: stats.total_stops,
      color: 'bg-red-100 text-red-600'
    },
    {
      icon: TrendingUp,
      label: 'Viajes',
      value: stats.total_trips,
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: Building2,
      label: 'Agencias',
      value: stats.total_agencies,
      color: 'bg-purple-100 text-purple-600'
    }
  ];

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight mb-2" data-testid="stats-title">Estadísticas Avanzadas</h1>
        <p className="text-sm text-slate-600">Análisis detallado del feed GTFS con métricas de distancia y servicio</p>
      </div>

      {/* Basic Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metrics.map((metric, index) => (
          <Card key={index} className="p-6" data-testid={`metric-${metric.label.toLowerCase()}`}>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg ${metric.color} flex items-center justify-center`}>
                <metric.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-slate-600">{metric.label}</p>
                <p className="text-2xl font-bold">{metric.value.toLocaleString()}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Total Distance */}
      {advancedStats && (
        <Card className="p-6 mb-6 bg-gradient-to-r from-blue-50 to-cyan-50">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-blue-600 text-white flex items-center justify-center">
              <Ruler className="w-8 h-8" />
            </div>
            <div>
              <p className="text-sm text-slate-600 font-medium">Distancia Total de Red</p>
              <p className="text-4xl font-bold text-slate-900">{advancedStats.totalDistance} km</p>
              <p className="text-sm text-slate-500 mt-1">Calculado desde trazados de rutas (shapes)</p>
            </div>
          </div>
        </Card>
      )}

      {/* Stats by Agency */}
      {advancedStats?.agencyStats && advancedStats.agencyStats.length > 0 && (
        <Card className="p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Estadísticas por Operador
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Operador</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Rutas</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Distancia (km)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Viajes Totales</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Viajes/Semana</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {advancedStats.agencyStats.map((agency, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">{agency.agency_name}</td>
                    <td className="px-4 py-3 font-mono text-sm">{agency.total_routes}</td>
                    <td className="px-4 py-3 font-mono text-sm">{agency.total_distance}</td>
                    <td className="px-4 py-3 font-mono text-sm">{agency.total_trips}</td>
                    <td className="px-4 py-3 font-mono text-sm">{agency.weekly_trips}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Stats by Route */}
      {advancedStats?.routeStats && advancedStats.routeStats.length > 0 && (
        <Card className="p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <RouteIcon className="w-5 h-5" />
            Estadísticas por Ruta
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Ruta</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Distancia (km)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Viajes Totales</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Viajes/Semana</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {advancedStats.routeStats.map((route, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">{route.route_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{route.route_id}</td>
                    <td className="px-4 py-3 font-mono text-sm">
                      {route.distance > 0 ? route.distance : '-'}
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">{route.total_trips}</td>
                    <td className="px-4 py-3 font-mono text-sm">{route.weekly_trips}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Service Periods */}
      {advancedStats?.servicePeriods && advancedStats.servicePeriods.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Períodos de Servicio
          </h3>
          <div className="space-y-4">
            {advancedStats.servicePeriods.map((service, idx) => (
              <div key={idx} className="border rounded-lg p-4 hover:bg-slate-50">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold">{service.service_id}</p>
                    <p className="text-sm text-slate-600">
                      {service.start_date} - {service.end_date}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Viajes</p>
                    <p className="text-xl font-bold">{service.total_trips}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {service.days_active.map((day, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {day}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Summary */}
      {stats.date_range && (
        <Card className="p-6 mt-6">
          <h3 className="font-semibold text-lg mb-4">Resumen del Feed</h3>
          <div className="space-y-2 text-sm">
            <p className="text-slate-600">
              Este feed GTFS contiene <span className="font-semibold text-slate-800">{stats.total_routes}</span> rutas
              con <span className="font-semibold text-slate-800">{stats.total_stops}</span> paradas
              y <span className="font-semibold text-slate-800">{stats.total_trips}</span> viajes programados.
            </p>
            {advancedStats && (
              <p className="text-slate-600">
                La red tiene una extensión total de <span className="font-semibold text-slate-800">{advancedStats.totalDistance} kilómetros</span>.
              </p>
            )}
            {stats.total_agencies > 0 && (
              <p className="text-slate-600">
                Gestionado por <span className="font-semibold text-slate-800">{stats.total_agencies}</span> {stats.total_agencies === 1 ? 'agencia' : 'agencias'}.
              </p>
            )}
            <p className="text-slate-600">
              El servicio está programado desde el <span className="font-mono text-slate-800">{stats.date_range.start}</span> hasta el <span className="font-mono text-slate-800">{stats.date_range.end}</span>.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default StatsPage;